import fs from 'node:fs'
import path from 'node:path'
import { findConfigFile, loadConfigFile } from '@core-engine/config/resolver.js'
import type { BetterstartConfigFile, ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import { detectNextMajorVersion } from '@next-adapter/config/detect.js'
import {
  DEFAULT_ADMIN_NAMESPACE,
  deriveMigrationsDir,
  deriveNamespacePaths,
  validateAdminNamespace
} from '@next-adapter/config/namespace.js'

const unsupportedStateMessage =
  'admin.config.ts must use the current BetterStart config shape with framework: "next" and frameworkConfig.next. Old top-level srcDir, nextMajorVersion, and paths config is no longer supported.'

export function getDefaultConfig(
  srcDir: boolean,
  nextMajorVersion = 16
): ResolvedBetterstartConfig {
  const namespace = DEFAULT_ADMIN_NAMESPACE
  const paths = deriveNamespacePaths(namespace, srcDir)

  return {
    framework: 'next',
    frameworkConfig: {
      next: {
        namespace,
        srcDir,
        version: nextMajorVersion,
        paths
      }
    },
    srcDir,
    nextMajorVersion,
    paths,
    database: {
      provider: 'postgres',
      migrationsDir: deriveMigrationsDir(namespace)
    },
    plugins: {
      installed: []
    },
    integrations: {
      installed: []
    },
    email: {
      provider: 'none'
    },
    storage: {
      provider: 'local'
    },
    linter: 'biome',
    generated: {
      entities: [],
      singles: [],
      forms: []
    }
  }
}

function assertNextConfigShape(config: BetterstartConfigFile): void {
  const candidate = config as BetterstartConfigFile & {
    srcDir?: unknown
    nextMajorVersion?: unknown
    paths?: unknown
  }

  if (
    candidate.srcDir !== undefined ||
    candidate.nextMajorVersion !== undefined ||
    candidate.paths !== undefined ||
    config.framework !== 'next' ||
    !config.frameworkConfig?.next
  ) {
    throw new Error(unsupportedStateMessage)
  }
}

export async function resolveConfig(cwd?: string): Promise<ResolvedBetterstartConfig> {
  const workingDir = cwd ?? process.cwd()
  const configPath = findConfigFile(workingDir)
  const nextMajorVersion = detectNextMajorVersion(workingDir)
  const hasSrc = fs.existsSync(path.join(workingDir, 'src'))
  const defaultConfig = getDefaultConfig(hasSrc, nextMajorVersion)

  if (!configPath) {
    return defaultConfig
  }

  const loadedConfig = await loadConfigFile(configPath)
  assertNextConfigShape(loadedConfig)
  const loadedNextConfig = loadedConfig.frameworkConfig.next
  const loadedDatabase = loadedConfig.database
  const namespace = validateAdminNamespace(loadedNextConfig.namespace ?? DEFAULT_ADMIN_NAMESPACE)
  const nextPaths = {
    ...deriveNamespacePaths(namespace, loadedNextConfig.srcDir ?? defaultConfig.srcDir),
    ...loadedNextConfig.paths
  }
  const nextConfig = {
    ...defaultConfig.frameworkConfig.next,
    ...loadedNextConfig,
    namespace,
    version: nextMajorVersion,
    paths: nextPaths
  }
  const config: ResolvedBetterstartConfig = {
    ...defaultConfig,
    ...loadedConfig,
    framework: 'next',
    frameworkConfig: {
      next: nextConfig
    },
    srcDir: nextConfig.srcDir,
    nextMajorVersion,
    paths: nextPaths,
    database: {
      provider: 'postgres',
      migrationsDir: loadedDatabase.migrationsDir
    },
    plugins: loadedConfig.plugins ?? defaultConfig.plugins,
    integrations: loadedConfig.integrations ?? defaultConfig.integrations,
    email: loadedConfig.email ?? defaultConfig.email,
    storage: loadedConfig.storage ?? defaultConfig.storage,
    generated: {
      ...defaultConfig.generated,
      ...loadedConfig.generated
    }
  }
  return config
}
